import { useQuery } from "@tanstack/react-query";
import { FolderTree } from "lucide-react";
import { groupsApi } from "@/api/client";

interface GroupSelectorProps {
  value: string | null;
  onChange: (slug: string | null) => void;
  includeAll?: boolean;
  includeUngrouped?: boolean;
  className?: string;
  label?: string;
}

export default function GroupSelector({
  value,
  onChange,
  includeAll = true,
  includeUngrouped = false,
  className = "",
  label,
}: GroupSelectorProps) {
  const { data, isLoading } = useQuery({
    queryKey: ["groups"],
    queryFn: groupsApi.list,
  });

  const groups = data?.groups ?? [];

  return (
    <div className={className}>
      {label && <label className="label">{label}</label>}
      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <FolderTree className="h-4 w-4 text-gray-400" />
        </div>
        <select
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value || null)}
          className="input pl-10"
          disabled={isLoading}
        >
          {includeAll && <option value="">All Groups</option>}
          {includeUngrouped && (
            <option value="__ungrouped__">Ungrouped</option>
          )}
          {groups.map((group) => (
            <option key={group.id} value={group.slug}>
              {group.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

interface GroupSelectFieldProps {
  value: string | null;
  onChange: (groupId: string | null) => void;
  className?: string;
  label?: string;
}

export function GroupSelectField({
  value,
  onChange,
  className = "",
  label = "Group (optional)",
}: GroupSelectFieldProps) {
  const { data, isLoading } = useQuery({
    queryKey: ["groups"],
    queryFn: groupsApi.list,
  });

  const groups = data?.groups ?? [];

  return (
    <div className={className}>
      <label className="label">{label}</label>
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value || null)}
        className="input"
        disabled={isLoading}
      >
        <option value="">No group</option>
        {groups.map((group) => (
          <option key={group.id} value={group.id}>
            {group.name}
          </option>
        ))}
      </select>
      <p className="mt-1 text-xs text-gray-500">
        Assign to a group for better organization
      </p>
    </div>
  );
}
